import React, { useCallback, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { PAGE_SIZE_OPTIONS } from '../../../../../utils/tableConstants';

const InvoiceTable = ({
    invoiceData,
    isLoadingInvoices,
    onRowCheckboxChange,
    onSelectAllChange,
    formatCurrency,
    onInfoClick,
    isProcessingApproval,
    totalRecords,
    lazyParams,
    onPageChange
}) => {
    const { th } = useLanguage();

    // Client-side sort state
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);

    // formatCurrency wrapper to use row currency
    const formatCurrencyWithRowData = (amount, rowData) => {
        return formatCurrency(amount, rowData?.currency || 'TRY');
    };

    // Client-side sorting
    const sortedData = useMemo(() => {
        if (!sortField || sortOrder === 0) return invoiceData;

        return [...invoiceData].sort((a, b) => {
            let aValue = a?.[sortField];
            let bValue = b?.[sortField];

            if (aValue == null && bValue == null) return 0;
            if (aValue == null) return sortOrder;
            if (bValue == null) return -sortOrder;

            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (sortField.toLowerCase().includes('date')) {
                aValue = new Date(aValue);
                bValue = new Date(bValue);
            }

            if (typeof aValue === 'number' && typeof bValue === 'number') {
                return sortOrder * (aValue - bValue);
            }

            if (aValue < bValue) return -sortOrder;
            if (aValue > bValue) return sortOrder;
            return 0;
        });
    }, [invoiceData, sortField, sortOrder]);

    const handleSort = useCallback((e) => {
        setSortField(e.sortField);
        setSortOrder(e.sortOrder);
    }, []);

    const checkboxTemplate = useCallback((rowData) => (
        <Checkbox
            checked={rowData.selected || false}
            onChange={(e) => onRowCheckboxChange(e, rowData)}
            disabled={isProcessingApproval}
        />
    ), [onRowCheckboxChange, isProcessingApproval]);

    const selectAllTemplate = useCallback(() => {
        const allSelected = invoiceData.length > 0 && invoiceData.every(item => item.selected);
        return (
            <Checkbox
                checked={allSelected}
                onChange={onSelectAllChange}
                disabled={isProcessingApproval}
            />
        );
    }, [invoiceData, onSelectAllChange, isProcessingApproval]);

    const infoButtonTemplate = useCallback((rowData) => (
        <Button
            icon="pi pi-info-circle"
            className="p-button-rounded p-button-text p-button-sm"
            onClick={() => onInfoClick(rowData.id)}
            tooltip={th('invoiceApproval.table.viewDetails')}
            tooltipOptions={{ position: 'top' }}
            disabled={isProcessingApproval}
        />
    ), [onInfoClick, th, isProcessingApproval]);

    const statusBodyTemplate = useCallback((rowData) => {
        const statusMap = {
            0: th('invoiceApproval.status.pending'),
            1: th('invoiceApproval.status.approved'),
            2: th('invoiceApproval.status.rejected')
        };
        return statusMap[rowData.invoiceStatus] || th('invoiceApproval.status.unknown');
    }, [th]);

    return (
        <DataTable
            value={sortedData}
            loading={isLoadingInvoices}
            emptyMessage={isLoadingInvoices ? th('common.loading') : th('invoiceApproval.table.noPendingRecords')}
            paginator
            rows={lazyParams.rows}
            first={lazyParams.first}
            totalRecords={totalRecords}
            onPage={onPageChange}
            onSort={handleSort}
            sortField={sortField}
            sortOrder={sortOrder}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            className="p-datatable-sm"
            stripedRows
            showGridlines
            dataKey="id"
            lazy
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            currentPageReportTemplate="{first} - {last} / {totalRecords}"
            sortMode="single"
        >
            {/* --- SELECTION AND MAIN INFO --- */}
            <Column
                header={selectAllTemplate}
                body={checkboxTemplate}
                style={{ width: '3rem' }}
                frozen
            />
            <Column
                header={th('invoiceApproval.table.detail')}
                body={infoButtonTemplate}
                style={{ width: '4rem' }}
                frozen
            />
            <Column
                field="warrantyClaimNo"
                header={th('invoiceApproval.table.warrantyClaimNo')}
                sortable
                style={{ minWidth: '150px' }}
            />
            <Column
                field="dealerName"
                header={th('invoiceApproval.table.dealerName')}
                sortable
                style={{ minWidth: '200px' }}
            />
            <Column
                field="branchCode"
                header={th('invoiceApproval.table.branchCode')}
                sortable
                style={{ minWidth: '120px' }}
            />
            <Column
                field="warrantyType"
                header={th('invoiceApproval.table.warrantyType')}
                sortable
                style={{ minWidth: '120px' }}
            />
            <Column
                field="guaranteeType"
                header={th('invoiceApproval.table.guaranteeType')}
                sortable
                style={{ minWidth: '120px' }}
            />

            {/* --- COST DETAILS --- */}
            <Column
                field="partsTotal"
                header={th('invoiceApproval.table.partsTotal')}
                sortable
                body={(rowData) => formatCurrencyWithRowData(rowData.partsTotal, rowData)}
                style={{ minWidth: '150px' }}
            />
            <Column
                field="laborTotal"
                header={th('invoiceApproval.table.laborTotal')}
                sortable
                body={(rowData) => formatCurrencyWithRowData(rowData.laborTotal, rowData)}
                style={{ minWidth: '150px' }}
            />
            <Column
                field="othersTotal"
                header={th('invoiceApproval.table.othersTotal')}
                sortable
                body={(rowData) => formatCurrencyWithRowData(rowData.othersTotal, rowData)}
                style={{ minWidth: '150px' }}
            />
            <Column
                field="totalClaimAmount"
                header={th('invoiceApproval.table.totalClaimAmount')}
                sortable
                body={(rowData) => formatCurrencyWithRowData(rowData.totalClaimAmount, rowData)}
                style={{ minWidth: '180px', fontWeight: 'bold' }}
            />
        </DataTable>
    );
};

export default InvoiceTable;